import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';
import { Button, FormInput, Message } from '@getro/rombo';

import BackupCodeToggle from './BackupCodeToggle';
import MfaDigitInput from './MfaDigitInput';
import { backupCodeSchema, totpCodeSchema } from './schemas';
import { mapMfaSubmitError } from './mfaSubmitErrors';

const SUBMIT_ERROR_FALLBACK = 'There was an unexpected error, please try again or contact us.';
const BACKUP_CODE_HELP_TEXT =
  'If you cannot access your authenticator app, use one of the one-time codes you saved when you set up two-factor authentication';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;

  form {
    width: 100%;
  }
`;

const BackupCodeSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  gap: 8px;
`;

const SubmitError = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const TotpError = styled(SubmitError)``;

const MfaCodeInput = ({
  onSubmit,
  allowBackupCode,
  disabled: disabledProp,
  dataCyPrefix,
  showSubmitButton,
  onModeChange,
  formKey,
}) => {
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [totpFocusToken, setTotpFocusToken] = useState(0);

  const switchMode = (nextUseBackupCode, resetForm) => {
    setUseBackupCode(nextUseBackupCode);
    onModeChange?.(nextUseBackupCode);
    resetForm({ values: { code: '' }, status: {} });
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setStatus }) => {
    setStatus({});

    try {
      await onSubmit(values.code.trim());
    } catch (error) {
      const message = mapMfaSubmitError(error, SUBMIT_ERROR_FALLBACK, { useBackupCode });

      if (!useBackupCode) {
        resetForm({
          values: { code: '' },
          errors: {},
          touched: {},
          status: { totpSubmitError: message },
        });
        setTotpFocusToken((token) => token + 1);
      } else {
        setStatus({ submitError: message });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      key={formKey}
      initialValues={{ code: '' }}
      enableReinitialize
      validationSchema={useBackupCode ? backupCodeSchema : totpCodeSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        setFieldError,
        errors,
        touched,
        isSubmitting,
        submitForm,
        resetForm,
        setStatus,
        status,
      }) => {
        const disabled = disabledProp || isSubmitting;
        const totpFieldName = dataCyPrefix ? `${dataCyPrefix}-code` : 'mfa-code';
        const submitError = status?.submitError;
        const totpErrorMessage = status?.totpSubmitError || (touched.code && errors.code ? errors.code : undefined);

        const clearTotpErrors = () => {
          if (errors.code) {
            setFieldError('code', undefined);
          }
          if (status?.totpSubmitError) {
            setStatus({});
          }
        };

        const submitWithCode = (code) => setFieldValue('code', code).then(() => submitForm());

        return (
          <Root>
            <Form>
              {useBackupCode ? (
                <BackupCodeSection>
                  <Field name="code">
                    {({ field, form }) => (
                      <FormInput
                        {...field}
                        form={form}
                        type="text"
                        label="Backup code"
                        helpText={BACKUP_CODE_HELP_TEXT}
                        autoComplete="one-time-code"
                        autoFocus
                        data-cy={dataCyPrefix ? `${dataCyPrefix}-code` : undefined}
                        onChange={(event) => {
                          field.onChange(event);
                          if (form.status?.submitError) {
                            form.setStatus({});
                          }
                        }}
                      />
                    )}
                  </Field>
                  <Button
                    width="100%"
                    justifyContent="center"
                    type="submit"
                    loading={disabled}
                    data-cy={dataCyPrefix ? `${dataCyPrefix}-submit` : undefined}
                  >
                    Submit
                  </Button>
                </BackupCodeSection>
              ) : (
                <MfaDigitInput
                  name={totpFieldName}
                  value={values.code}
                  onChange={(code) => {
                    setFieldValue('code', code);
                    clearTotpErrors();
                  }}
                  onComplete={submitWithCode}
                  disabled={disabled}
                  loading={isSubmitting || disabledProp}
                  ariaLabel="Authentication code"
                  focusToken={totpFocusToken}
                  onUseBackupCode={allowBackupCode ? () => switchMode(true, resetForm) : undefined}
                  backupCodeToggleDataCy={dataCyPrefix ? `${dataCyPrefix}-toggle-backup` : undefined}
                />
              )}
              {showSubmitButton && !useBackupCode && (
                <Button
                  mt="16px"
                  width="100%"
                  justifyContent="center"
                  type="submit"
                  loading={disabled}
                  size="large"
                  data-cy={dataCyPrefix ? `${dataCyPrefix}-submit` : undefined}
                >
                  Submit
                </Button>
              )}
            </Form>
            {useBackupCode && allowBackupCode && (
              <BackupCodeToggle
                useBackupCode
                onToggle={() => switchMode(false, resetForm)}
                data-cy={dataCyPrefix ? `${dataCyPrefix}-toggle-backup` : undefined}
              />
            )}
            {useBackupCode && submitError && (
              <SubmitError>
                <Message type="error" content={submitError} />
              </SubmitError>
            )}
            {!useBackupCode && totpErrorMessage && (
              <TotpError>
                <Message type="error" content={totpErrorMessage} />
              </TotpError>
            )}
          </Root>
        );
      }}
    </Formik>
  );
};

MfaCodeInput.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  allowBackupCode: PropTypes.bool,
  disabled: PropTypes.bool,
  dataCyPrefix: PropTypes.string,
  showSubmitButton: PropTypes.bool,
  onModeChange: PropTypes.func,
  formKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MfaCodeInput.defaultProps = {
  allowBackupCode: true,
  disabled: false,
  dataCyPrefix: undefined,
  showSubmitButton: false,
  onModeChange: undefined,
  formKey: undefined,
};

MfaCodeInput.displayName = 'MfaCodeInput';

export default MfaCodeInput;
